import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { TTeamManagementForm, UseCreateTeamReturn } from '../team-management.types';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { StatusEnum, TeamsInterface, useCreateTeamsMutation } from '@/types/generated/graphql';
import { notification } from 'antd';
import { cacheAddTeamV2 } from '../team-handlers';

const defaultValues = {
  name: '',
  members: [],
};

export const useCreateTeam = (): UseCreateTeamReturn => {
  const [openAddModal, setOpenAddModal] = useState(false);

  const handleOpenAdd = () => setOpenAddModal(true);
  const handleCloseAdd = () => setOpenAddModal(false);

  const createForm = useForm<TTeamManagementForm>({
    resolver: zodResolver(
      z.object({
        name: z.string({ required_error: 'Name is required.' }).trim().min(1, 'Name is required.'),
        members: z
          .array(z.number({ required_error: 'Each member id must be number.' }))
          .min(1, { message: 'At least one member is required.' }),
      })
    ),
    defaultValues,
  });

  const { handleSubmit: handleCreateSubmit, control: addTeamControl, reset } = createForm;

  const handleOpenAddModal = () => {
    handleOpenAdd();
  };

  const handleCloseAddModal = () => {
    handleCloseAdd();
    reset(defaultValues);
  };

  const [createTeam, { loading: createTeamLoading }] = useCreateTeamsMutation({
    onCompleted: (res) => {
      notification.success({ message: res.createTeams?.message });
      cacheAddTeamV2(res.createTeams?.data as TeamsInterface);
      handleCloseAddModal();
    },
    onError: (error) => {
      notification.error({ message: error?.message });
    },
  });

  const handleCreateTeamSubmit = handleCreateSubmit(async function submitHandler(data: TTeamManagementForm) {
    createTeam({
      variables: {
        input: {
          name: data.name,
          inputTeamMembersInterface: data.members.map((userId) => ({
            userId: parseInt(userId),
            status: StatusEnum.active,
          })),
          description: 'n/a',
          status: StatusEnum.active,
        },
      },
    });
  });

  return {
    openAddModal,
    handleOpenAdd,
    handleCloseAdd,
    handleOpenAddModal,
    handleCloseAddModal,
    createForm,
    createTeam,
    addTeamControl,
    handleCreateTeamSubmit,
    createTeamLoading,
  };
};
